import type { AxiosRequestConfig, AxiosResponse } from "axios";
import { defineStore } from "pinia";
import type { Ref } from "vue";
import { computed, ref } from "vue";
import type { ArticleResponse } from "../../responses/ArticleResponse.ts";
import type { FaqResponse } from "../../responses/FaqResponse.ts";
import type { PageResponse } from "../../responses/PageResponse.ts";
import type { ProductResponse } from "../../responses/ProductResponse.ts";
import { api } from "../scripts/api";

interface SearchResult<T> {
    count: number;
    items: T[];
}

interface SearchResults {
    pages?: SearchResult<PageResponse>;
    products?: SearchResult<ProductResponse>;
    articles?: SearchResult<ArticleResponse>;
    faqs?: SearchResult<FaqResponse>;
}

export const useSearchStore = defineStore("search", () => {
    const results: Ref<SearchResults | null> = ref(null);
    const isLoading: Ref<boolean> = ref(false);
    const toggleSearch: Ref<boolean> = ref(false);

    const totalResults = computed(() => {
        if (results.value === null) return 0;

        let count = 0;

        for (const [_key, value] of Object.entries(results.value)) {
            count += value.count;
        }

        return count;
    });

    let abortController: AbortController | null = null;

    const getSearchResults = (query: string, type: string) => {
        if (query.length > 1) {
            isLoading.value = true;

            if (abortController != null) {
                abortController.abort();
            }

            abortController = new AbortController();
            try {
                api()
                    .post(
                        `/site/search/`,
                        {
                            query: query,
                            type: type,
                        },
                        {
                            signal: abortController.signal,
                        } as AxiosRequestConfig,
                    )
                    .then((response) => {
                        results.value = response.data;
                    });
            } finally {
                setTimeout(() => {
                    isLoading.value = false;
                }, 250);
            }
        } else {
            results.value = null;
        }
    };

    return {
        isLoading,
        results,
        totalResults,
        toggleSearch,
        getSearchResults,
    };
});
